import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Alert,
    Paper,
    Button
} from '@mui/material';
import {
    AutoAwesome as AiIcon
} from '@mui/icons-material';
import QuizGenerator from '../QuizGenerator';

interface FillInBlankEditorProps {
    content: any;
    onChange: (content: any) => void;
}

const FillInBlankEditor: React.FC<FillInBlankEditorProps> = ({ content, onChange }) => {
    const [generatorOpen, setGeneratorOpen] = useState(false);

    const handleQuestionsGenerated = (generatedQuestions: any[]) => {
        // Combine all generated fill-in-blank texts
        const combinedText = generatedQuestions.map((gq) => gq.text).join('\n\n');
        const currentText = content?.text || '';
        const newText = currentText ? `${currentText}\n\n${combinedText}` : combinedText;
        onChange({ ...content, text: newText });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Lückentext</Typography>
                <Button
                    variant="outlined"
                    startIcon={<AiIcon />}
                    onClick={() => setGeneratorOpen(true)}
                >
                    KI-Generator
                </Button>
            </Box>

            <Alert severity="info">
                Verwenden Sie <code>{'{{Antwort}}'}</code> um Lücken zu markieren.
                Mehrere richtige Antworten können mit <code>{'{{Antwort1|Antwort2}}'}</code> definiert werden.
            </Alert>

            <TextField
                label="Text mit Lücken"
                fullWidth
                multiline
                rows={10}
                value={content?.text || ''}
                onChange={(e) => onChange({ ...content, text: e.target.value })}
                placeholder="Beispiel: Die Hauptstadt von Deutschland ist {{Berlin|berlin}}."
                helperText="Markieren Sie Lücken mit doppelten geschweiften Klammern"
            />

            <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                <Typography variant="subtitle2" gutterBottom>
                    Beispiel:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Der größte Planet in unserem Sonnensystem ist {'{{Jupiter|jupiter}}'}.
                    <br />
                    Die Erde hat {'{{einen|1}}'} Mond.
                </Typography>
            </Paper>

            <TextField
                label="Hinweis (optional)"
                fullWidth
                multiline
                rows={2}
                value={content?.hint || ''}
                onChange={(e) => onChange({ ...content, hint: e.target.value })}
                helperText="Wird den Lernenden als Hilfestellung angezeigt"
            />

            <TextField
                label="Erklärung (optional)"
                fullWidth
                multiline
                rows={3}
                value={content?.explanation || ''}
                onChange={(e) => onChange({ ...content, explanation: e.target.value })}
                helperText="Wird nach der Beantwortung angezeigt"
            />

            <QuizGenerator
                open={generatorOpen}
                onClose={() => setGeneratorOpen(false)}
                onQuestionsGenerated={handleQuestionsGenerated}
                defaultQuestionType="fill_in_blank"
            />
        </Box>
    );
};

export default FillInBlankEditor;
